'use client';

import type React from 'react';
import { motion } from 'framer-motion';
import { useHeroImageContext } from '@/components/providers/HeroImageProvider';
import BackgroundImage from '@/components/ui/layout/background-image';
import { StaggeredText } from '@/components/ui/StaggeredText';
import { TypographyH1, TypographySmall } from '@/components/ui/typography';
import { cls, cn } from '@/lib/utils';
import {
  useFadeIn,
  useFadeInBottom,
  useStaggerFadeIn,
} from '../../animations';

interface HeroProperties {
  title?: string;
  /** @deprecated Use `subtitle` instead */
  desc?: string;
  subtitle?: string;
  children?: React.ReactNode;
  sideContent?: React.ReactNode;
  showGradient?: boolean;
  height?: string;
  align?: 'center' | 'left';
}

function Hero({
  title,
  desc,
  subtitle,
  showGradient = true,
  height = 'min-h-[100vh]',
  align = 'center',
  children,
  sideContent,
}: HeroProperties) {
  const resolvedSubtitle = subtitle ?? desc;
  const heroImageContext = useHeroImageContext();
  const staggerFadeIn = useStaggerFadeIn(1);
  const fadeInBottom = useFadeInBottom(20);
  const fadeIn = useFadeIn();

  const alignClasses =
    align === 'center'
      ? 'items-center justify-center text-center'
      : 'items-start justify-center text-left';

  return (
    <BackgroundImage
      src={heroImageContext.url ?? ''}
      alt="Hero Image of a Cornerstone Built House"
      fill
      sizes="100vw"
      overlay={cn(
        showGradient && 'bg-black/40',
      )}
      foreground={cn('w-full flex', height)}
      imageProps={{ showSkeleton: false }}
      motionProps={fadeIn}
    >
      <div
        className={cn(
          'flex flex-1 w-full mx-auto px-6 pt-[6vh] md:pt-[10vh] pb-[6vh] md:pb-[10vh]',
          sideContent
            ? 'flex-col lg:flex-row gap-8 lg:gap-12 max-w-6xl items-center'
            : cn('flex-col gap-6 max-w-3xl', alignClasses),
        )}
      >
        <div className={cn(sideContent && 'lg:flex-3 flex flex-col gap-6', !sideContent && 'flex flex-col gap-6 w-full', sideContent ? 'items-start text-left' : alignClasses)}>
          <motion.div
            className={cn('flex flex-col gap-4', sideContent ? 'items-start' : (align === 'center' ? 'items-center' : 'items-start'))}
            {...staggerFadeIn}
          >
            {title && (
              <TypographyH1 className={cls`text-white drop-shadow-lg`}>
                <StaggeredText>{title}</StaggeredText>
              </TypographyH1>
            )}

            {resolvedSubtitle && (
              <TypographySmall className="hero-description text-white/90 drop-shadow-md max-w-xl">
                {resolvedSubtitle}
              </TypographySmall>
            )}
          </motion.div>

          {children && (
            <motion.div
              className={cn(
                'flex gap-4 flex-wrap',
                sideContent ? 'justify-start' : (align === 'center' ? 'justify-center' : 'justify-start'),
              )}
              {...{
                ...fadeInBottom,
                transition: { ...(fadeInBottom.transition ?? {}), delay: 1 },
              }}
            >
              {children}
            </motion.div>
          )}
        </div>

        {sideContent && (
          <motion.div
            className="lg:flex-2 w-full self-center"
            {...{
              ...fadeInBottom,
              transition: { ...(fadeInBottom.transition ?? {}), delay: 0.5 },
            }}
          >
            {sideContent}
          </motion.div>
        )}
      </div>
    </BackgroundImage>
  );
}

export default Hero;
